import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Calculator } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import TaxDesign, { GstSplitChecker } from "@/components/masters/TaxDesign";
import { EditMasterDialog } from "@/components/masters/EditMasterDialog";

export default function TaxCodes() {
  const { can } = usePermissions();
  const canCreateTaxCode = can("TAX_CODES", "create");
  const canEditTaxCode = can("TAX_CODES", "edit");
  const [taxCodes, setTaxCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/tax-codes");
      setTaxCodes(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (taxCode) => {
    try {
      await api.patch(`/masters/tax-codes/${taxCode.id}`, { is_active: !taxCode.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const shown = useMemo(() => {
    if (!search) return taxCodes;
    const q = search.toLowerCase();
    return taxCodes.filter((t) =>
      (t.code || "").toLowerCase().includes(q) ||
      (t.name || "").toLowerCase().includes(q)
    );
  }, [taxCodes, search]);

  return (
    <div className="space-y-6" data-testid="tax-codes-page">
      <TaxDesign taxCodes={taxCodes} shown={shown} loading={loading} search={search} onSearch={setSearch} canManage={canEditTaxCode}
        onToggle={toggleActive} onView={setSelected} onChanged={load}
        createAction={canCreateTaxCode && <CreateTaxCodeDialog onCreated={load} />} />

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`Tax Code · ${selected?.code}`}
      >
        <DrawerSection label="Tax Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="Rate" value={selected?.rate != null ? `${selected.rate}%` : null} mono />
          <DrawerField label="Is GST" value={selected?.is_gst ? "Yes" : "No"} />
          <DrawerField label="CGST" value={selected?.cgst_rate != null ? `${selected.cgst_rate}%` : null} mono />
          <DrawerField label="SGST" value={selected?.sgst_rate != null ? `${selected.sgst_rate}%` : null} mono />
          <DrawerField label="IGST" value={selected?.igst_rate != null ? `${selected.igst_rate}%` : null} mono />
          <DrawerField label="Status" value={selected?.is_active ? "Active" : "Inactive"} />
        </DrawerSection>
      </DetailDrawer>

      {taxCodes.length > 0 && <GstSplitChecker taxCodes={taxCodes} />}
    </div>
  );
}

function GstSplitCalculator({ taxCodes }) {
  const [taxCodeId, setTaxCodeId] = useState(taxCodes[0]?.id || "");
  const [sellerState, setSellerState] = useState("Rajasthan");
  const [buyerState, setBuyerState] = useState("Rajasthan");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/masters/tax-codes/${taxCodeId}/gst-split`, {
        seller_state: sellerState || null, buyer_state: buyerState || null,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="gst-split-calculator">
      <div className="mb-3 flex items-center gap-2 text-muted-foreground">
        <Calculator className="h-4 w-4" />
        <h2 className="text-sm font-semibold uppercase tracking-wider">GST split checker</h2>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <Label>Tax code</Label>
          <Select value={taxCodeId} onValueChange={setTaxCodeId}>
            <SelectTrigger data-testid="gst-tax-code-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{taxCodes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Seller state</Label>
          <Input data-testid="gst-seller-state-input" value={sellerState} onChange={(e) => setSellerState(e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label>Buyer state</Label>
          <Input data-testid="gst-buyer-state-input" value={buyerState} onChange={(e) => setBuyerState(e.target.value)} className="mt-1.5" />
        </div>
        <div className="flex items-end">
          <Button type="button" disabled={!taxCodeId || loading} onClick={calculate} data-testid="gst-split-calculate-button" className="w-full">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Calculate
          </Button>
        </div>
      </div>
      {result && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="gst-split-result">
          <ResultStat label="CGST" value={`${result.cgst_rate}%`} />
          <ResultStat label="SGST" value={`${result.sgst_rate}%`} />
          <ResultStat label="IGST" value={`${result.igst_rate}%`} />
          <ResultStat label="Type" value={result.is_inter_state ? "Inter-state" : "Intra-state"} />
        </div>
      )}
    </div>
  );
}

function ResultStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3 text-center">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold text-foreground">{value}</p>
    </div>
  );
}

function CreateTaxCodeDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", rate: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/tax-codes", { ...form, rate: Number(form.rate) });
      toast.success(`Tax code ${form.code} created`);
      setForm({ code: "", name: "", rate: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-tax-code-button" className="h-11 gap-2 rounded-lg bg-[#910029] px-5 text-sm font-semibold text-white shadow-sm hover:bg-[#7A0022]"><Plus className="h-4 w-4" /> New Tax Code</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Tax Code</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-tax-code-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input placeholder="Automatic if blank" data-testid="tax-code-code-input" value={form.code} onChange={set("code")} className="mt-1.5" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Rate (%)</Label>
              <Input required type="number" step="0.01" data-testid="tax-code-rate-input" value={form.rate} onChange={set("rate")} className="mt-1.5" placeholder="18" />
              <FieldError error={errors.rate} />
            </div>
          </div>
          <div>
            <Label>Name</Label>
            <Input required data-testid="tax-code-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="GST 18%" />
            <FieldError error={errors.name} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-tax-code-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
